import {
  FormControl,
  Input,
  useModalContext,
} from '@chakra-ui/react';
import { roundToNearestMinutes } from 'date-fns';
import React, {
  ComponentPropsWithoutRef,
  useEffect,
  useRef,
} from 'react';
import {
  useField,
} from 'react-final-form';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import FieldError from '../FieldError';
import FieldLabel from '../FieldLabel';

type Props = {
  name: string;
  validate?: (val: string) => any;
  label?: string;
  placeholder?: string;
  minDate?: Required<Parameters<typeof flatpickr>>[1]['minDate'];
}

const DateTimeField = ({ name, validate, label, placeholder, minDate }: Props) => {
  /// Final-Form Field
  const field = useField(
    name,
    {
      validate,
    },
  );

  let modalContext: ReturnType<typeof useModalContext>;
  try {
    modalContext = useModalContext();
  } catch (error) {
    // I guess we're not in a modal
  }

  /// Flatpickr Instance
  const flatpickrRef = useRef<HTMLInputElement | null>(null);
  useEffect(
    // If the ref actually changes, this _should_ bind a new Flatpickr instance and continue seamlessly...
    () => {
      if (flatpickrRef.current) {
        const quantizedMinDate = minDate
          ? roundToNearestMinutes(new Date(minDate), { nearestTo: 15, roundingMethod: 'floor' })
          : undefined;
        const instance = flatpickr(
          flatpickrRef.current,
          {
            enableTime: true,
            altInput: true,
            altFormat: 'l F j, Y at h:i K',
            minuteIncrement: 15,
            dateFormat: 'Z', // ISO
            // Update Final-Form value when Flatpickr value changes
            onChange: (dates) => {
              const dateStr = new Date(dates[0]).toISOString();
              field.input.onChange(dateStr);
            },
            // Fake "touched" state...
            onClose: () => {
              field.input.onBlur();
            },
            minDate: quantizedMinDate,
            appendTo: modalContext?.dialogRef.current || undefined,
          },
        );
        return () => {
          // clean up on unmount
          instance.destroy();
        };
      }
    },
    [flatpickrRef.current],
  );

  /// Update Flatpickr value when Final-Form value changes
  useEffect(
    () => {
      const quantizedDate = roundToNearestMinutes(
        new Date(field.input.value),
        {
          nearestTo: 15,
        },
      );
      if (!Number.isNaN(quantizedDate.valueOf())) {
        // @ts-ignore
        // eslint-disable-next-line no-underscore-dangle
        flatpickrRef.current?._flatpickr?.setDate(quantizedDate.toISOString());
      }
    },
    [field.input.value],
  );

  return (
    <FormControl>
      <FieldLabel wrapLabel>{label}</FieldLabel>
      <Input ref={flatpickrRef} placeholder={placeholder} />
      <FieldError name={name} alwaysShow={false} />
    </FormControl>
  );
};

export default DateTimeField;
